import { useMemo } from 'react';
import { useVault } from '../lib/vault';
import { WelcomeText, useWelcome } from '../components/Welcome';
import {
  BASE_CURRENCY,
  CYCLE_LABEL,
  annualMoney,
  daysUntil,
  formatMoney,
  oneTimeMoney,
  periodMoney,
  sumMoney,
  type Money,
} from '@shared/money';
import type { ItemForUser, PaymentMethod } from '@shared/types';
import { PAYMENT_LABEL } from '@shared/types';
import type { View } from '../app';
import { plural } from '@shared/text';
import { affectedCount } from '../lib/duplicates';
import { Hint, HiddenHints } from '../components/Hint';
import { ItemIcon } from '../components/ItemIcon';

interface Bucket {
  name: string;
  /** Brutto aufs Jahr. Der Monatswert wird daraus abgeleitet, damit Verträge
   *  aller Rhythmen vergleichbar sind. */
  annualCents: number;
  count: number;
}

/** Zwölftel des Jahresbetrags - ein Mittelwert, keine tatsächliche Abbuchung. */
function monthlyAverage(annualCents: number): number {
  return Math.round(annualCents / 12);
}

/**
 * Was Mitglieder sehen. Keine Preise, keine Vertragswarnungen - dafuer alles,
 * was die eigene Arbeit betrifft: die eigenen Zugaenge, die eigenen Plaetze und
 * was davon gerade Aufmerksamkeit braucht.
 */
function MemberDashboard({ onNavigate }: { onNavigate: (v: View, ids?: string[]) => void }): JSX.Element {
  const { items, users, me, duplicates, duplicatesPending } = useVault();

  const mine = useMemo(() => {
    const uid = me?.uid ?? '';
    const lesbar = items.filter((i) => Boolean(i.wrappedKey));
    const plaetze = items.flatMap((item) =>
      item.assignments
        .filter((a) => a.openToAll || (a.assigneeUids ?? []).includes(uid))
        .map((a) => ({ item, assignment: a })),
    );
    const zuTun = lesbar.filter((i) => i.needsRotation);
    const verantwortlich = items.filter((i) => i.ownerUid === uid && i.status !== 'cancelled');
    const nurIch = lesbar.filter((i) => i.accessUids.length === 1);
    const fristen = verantwortlich
      .flatMap((item) => {
        const out: { item: ItemForUser; kind: 'renewal' | 'deadline'; days: number }[] = [];
        const renewal = daysUntil(item.renewalDate);
        const deadline = daysUntil(item.cancelBeforeDate);
        if (renewal !== null && renewal <= 60) out.push({ item, kind: 'renewal', days: renewal });
        if (deadline !== null && deadline <= 60) out.push({ item, kind: 'deadline', days: deadline });
        return out;
      })
      .sort((a, b) => a.days - b.days);
    return { lesbar, plaetze, zuTun, verantwortlich, nurIch, fristen };
  }, [items, me]);

  const nameOf = (uid: string | null): string => users.find((u) => u.uid === uid)?.name ?? '–';

  const willkommen = useWelcome();
  const vorname = (me?.name ?? '').split(' ')[0] ?? '';

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{vorname ? `Hallo ${vorname}` : 'Hallo'}</h1>
        </div>
      </div>

      {/* Zahlenkacheln standen hier einmal - für Mitglieder sagen sie nichts,
          was der Text und die Listen darunter nicht besser sagen. */}
      <div className="card welcome-lead">
        <WelcomeText text={willkommen.text} />
        <div className="row-tight" style={{ marginTop: 16 }}>
          <button className="btn-primary" onClick={() => onNavigate('vault')}>
            Zu deinen Zugängen
          </button>
          <span className="small muted">
            {mine.lesbar.length === 0
              ? 'Für dich ist noch nichts freigegeben.'
              : `${plural(mine.lesbar.length, 'Eintrag wurde', 'Einträge wurden')} mit dir geteilt${
                  mine.plaetze.length > 0
                    ? `, dazu ${plural(mine.plaetze.length, 'ein persönlicher Platz', 'persönliche Plätze')}`
                    : ''
                }.`}
          </span>
        </div>
      </div>

      {mine.zuTun.length > 0 ? (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="card-title">
            <h2>Das solltest du erledigen</h2>
            <button className="btn-sm" onClick={() => onNavigate('vault')}>
              Zu den Zugängen
            </button>
          </div>
          <table>
            <tbody>
              {mine.zuTun.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.title}</strong>
                    <div className="small muted">{item.rotationReason ?? 'Zugriff wurde entzogen'}</div>
                  </td>
                  <td className="num">
                    {item.url ? (
                      <a className="small" href={item.url} target="_blank" rel="noreferrer noopener">
                        beim Anbieter öffnen{'\u00a0↗'}
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="grid grid-2" style={{ marginTop: 14 }}>
        <div className="card">
          <div className="card-title">
            <h2>Mit dir geteilt</h2>
            <span className="small muted">{mine.lesbar.length}</span>
          </div>
          {mine.lesbar.length === 0 ? (
            <div className="empty">
              Es wurde noch nichts mit dir geteilt. Sag jemandem aus dem Team Bescheid, wer dich
              freischalten kann.
            </div>
          ) : (
            <table>
              <tbody>
                {[...mine.lesbar]
                  .sort((a, b) => a.title.localeCompare(b.title, 'de'))
                  .slice(0, 12)
                  .map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="row-tight">
                          <ItemIcon item={item} size={22} />
                          <div>
                            <strong>{item.title}</strong>
                            <div className="small muted">
                              {[item.category, item.username].filter(Boolean).join(' · ')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="num small muted">
                        {item.accessUids.length === 1
                          ? 'nur du'
                          : `${item.accessUids.length} Personen`}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <div className="card-title">
            <h2>Deine persönlichen Zugänge</h2>
          </div>
          {mine.plaetze.length === 0 ? (
            <div className="empty">Dir ist kein persönlicher Zugang zugewiesen.</div>
          ) : (
            <table>
              <tbody>
                {mine.plaetze.map(({ item, assignment }) => (
                  <tr key={`${item.id}-${assignment.id}`}>
                    <td>
                      <strong>{item.title}</strong>
                      <div className="small muted">
                        {assignment.label || 'Platz'}
                        {assignment.device ? ` · ${assignment.device}` : ''}
                      </div>
                    </td>
                    <td className="num small muted">
                      {assignment.openToAll
                        ? 'ganzes Team'
                        : (assignment.assigneeUids ?? []).length > 1
                          ? `mit ${(assignment.assigneeUids ?? []).length - 1} weiteren`
                          : 'nur du'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>


      {duplicates.groups.length > 0 ? (
        <Hint
          id="duplicates"
          tone="danger"
          count={affectedCount(duplicates)}
          showLabel="Betroffene zeigen"
          onShow={() =>
            onNavigate('vault', [...new Set(duplicates.groups.flatMap((g) => g.uses.map((u) => u.itemId)))])
          }
          title={
            <>
              {plural(duplicates.groups.length, 'Passwort wird', 'Passwörter werden')} mehrfach
              benutzt – an insgesamt {affectedCount(duplicates)} Stellen.
            </>
          }
        >
          {duplicates.groups.slice(0, 5).map((g, i) => (
            <div key={g.id} style={{ marginTop: i === 0 ? 0 : 4 }}>
              {g.uses.map((u) => `${u.title}${u.where === 'Eintrag' ? '' : ` (${u.where})`}`).join(' = ')}
            </div>
          ))}
          {duplicates.groups.length > 5 ? <div style={{ marginTop: 4 }}>…</div> : null}
          <p style={{ margin: '8px 0 0' }}>
            Wird eines davon bekannt, sind alle betroffen. Der Passwortgenerator im Editor macht
            daraus in zwei Klicks eigene Passwörter.
          </p>
        </Hint>
      ) : null}

      {mine.nurIch.length > 0 ? (
        <Hint
          id="singleAccess"
          count={mine.nurIch.length}
          onShow={() => onNavigate('vault', mine.nurIch.map((i) => i.id))}
          title={
            <>
              Bei {plural(mine.nurIch.length, 'Eintrag bist du', 'Einträgen bist du')} die einzige
              Person mit Zugriff.
            </>
          }
        >
          Verlierst du deine Master-Passphrase, ist das Passwort weg. Gib sie sicherheitshalber
          jemandem aus dem Team frei.
        </Hint>
      ) : null}

      <HiddenHints />

      {mine.fristen.length > 0 ? (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="card-title">
            <h2>Fristen deiner Verträge</h2>
            <span className="small muted">nächste 60 Tage</span>
          </div>
          <table>
            <tbody>
              {mine.fristen.map(({ item, kind, days }) => (
                <tr key={`${item.id}-${kind}`}>
                  <td>
                    <strong>{item.title}</strong>
                    <div className="small muted">
                      {kind === 'deadline' ? 'Kündigungsfrist' : 'Verlängerung'}
                    </div>
                  </td>
                  <td className="num">
                    <span className={days <= 14 ? 'badge badge-warn' : 'badge'}>
                      {days < 0 ? `${Math.abs(days)} Tage überfällig` : days === 0 ? 'heute' : `in ${days} Tagen`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="small muted" style={{ marginTop: 10 }}>
            Du bist hier als verantwortlich eingetragen. Kündigen oder verlängern läuft über die
            Verwaltung – sag rechtzeitig Bescheid. Zuständig: {nameOf(mine.fristen[0]?.item.ownerUid ?? null)}
          </p>
        </div>
      ) : null}
    </>
  );
}

export function DashboardPage({ onNavigate }: { onNavigate: (v: View, ids?: string[]) => void }): JSX.Element {
  const { items, users, can, duplicates, duplicatesPending } = useVault();
  const isAdmin = can('viewCosts');

  const stats = useMemo(() => {
    const paying = items.filter((i) => i.hasCost);
    const active = paying.filter((i) => i.status === 'active');
    const trials = paying.filter((i) => i.status === 'trial');
    const cancelled = paying.filter((i) => i.status === 'cancelled');
    const free = items.filter((i) => !i.hasCost);

    const recurring = active.filter((i) => i.cycle !== 'onetime');

    // Getrennt nach Abrechnungsrhythmus. Ein Jahresvertrag geht eben nicht
    // jeden Monat vom Konto ab - ihn anteilig unter "monatlich" zu führen,
    // wäre eine erfundene Zahl.
    const byCycle = (cycle: string): ItemForUser[] => recurring.filter((i) => i.cycle === cycle);
    const monatlich = byCycle('monthly');
    const jaehrlich = byCycle('yearly');
    const sonstige = recurring.filter((i) => i.cycle !== 'monthly' && i.cycle !== 'yearly');

    const monatlichMoney = sumMoney(monatlich.map(periodMoney));
    const jaehrlichMoney = sumMoney(jaehrlich.map(periodMoney));
    const sonstigeMoney = sumMoney(sonstige.map(periodMoney));
    const jahresMoney = sumMoney(recurring.map(annualMoney));

    const trialAnnual = sumMoney(trials.map((i) => annualMoney({ ...i, status: 'active' })));
    const purchases = active.filter((i) => i.cycle === 'onetime');
    const einmaligMoney = sumMoney(purchases.map(oneTimeMoney));

    const byCategory = new Map<string, Bucket>();
    for (const item of recurring) {
      const name = item.category.trim() || 'Ohne Kategorie';
      const bucket = byCategory.get(name) ?? { name, annualCents: 0, count: 0 };
      bucket.annualCents += annualMoney(item).grossCents;
      bucket.count += 1;
      byCategory.set(name, bucket);
    }
    const categories = [...byCategory.values()].sort((a, b) => b.annualCents - a.annualCents);

    const byPayment = new Map<PaymentMethod, Bucket>();
    for (const item of recurring) {
      const key = item.paymentMethod;
      const bucket = byPayment.get(key) ?? { name: PAYMENT_LABEL[key], annualCents: 0, count: 0 };
      bucket.annualCents += annualMoney(item).grossCents;
      bucket.count += 1;
      byPayment.set(key, bucket);
    }
    const payments = [...byPayment.entries()]
      .map(([method, bucket]) => ({ method, ...bucket }))
      .sort((a, b) => b.annualCents - a.annualCents);
    /** Enthält die Aufstellung Verträge, die nicht monatlich abgerechnet werden? */
    const gemischteRhythmen = recurring.some((i) => i.cycle !== 'monthly');

    // Abbuchungen nach Tag im Monat. Bewusst nur monatliche Verträge: Quartals-
    // und Jahresbeträge mit hineinzusummieren würde eine Zahl ergeben, die so
    // nie an einem Tag vom Konto geht. Gerechnet wird brutto - das ist der
    // Betrag, der tatsächlich abgebucht wird.
    const byDay = new Map<number, { day: number; items: ItemForUser[]; cents: number }>();
    for (const item of monatlich) {
      if (!item.billingDay) continue;
      const bucket = byDay.get(item.billingDay) ?? { day: item.billingDay, items: [], cents: 0 };
      bucket.items.push(item);
      bucket.cents += periodMoney(item).grossCents;
      byDay.set(item.billingDay, bucket);
    }
    const billingDays = [...byDay.values()].sort((a, b) => a.day - b.day);
    const withoutBillingDay = monatlich.filter((i) => !i.billingDay).length;

    const dated = items
      .filter((i) => i.status !== 'cancelled')
      .flatMap((item) => {
        const out: { item: ItemForUser; kind: 'renewal' | 'deadline'; days: number }[] = [];
        const renewal = daysUntil(item.renewalDate);
        const deadline = daysUntil(item.cancelBeforeDate);
        if (renewal !== null && renewal <= 60) out.push({ item, kind: 'renewal', days: renewal });
        if (deadline !== null && deadline <= 60) out.push({ item, kind: 'deadline', days: deadline });
        return out;
      })
      .sort((a, b) => a.days - b.days);

    // Bezahlte, aber unbesetzte Plaetze - direkt gespartes Geld.
    const idleSeats = items
      .filter((i) => i.kind !== 'shared' && i.hasCost && i.status === 'active' && (i.seats ?? 0) > 0)
      .map((item) => {
        const taken = item.assignments.filter((a) => a.openToAll || (a.assigneeUids ?? []).length > 0).length;
        const spare = (item.seats ?? 0) - taken;
        // Brutto aufs Jahr, damit es zu den Kacheln passt.
        const perSeat = Math.round(annualMoney(item).grossCents / Math.max(1, item.seats ?? 1));
        return { item, spare, wasteCents: Math.max(0, spare) * perSeat };
      })
      .filter((row) => row.spare > 0)
      .sort((a, b) => b.wasteCents - a.wasteCents);
    const idleWaste = idleSeats.reduce((sum, r) => sum + r.wasteCents, 0);

    const needsRotation = items.filter((i) => i.needsRotation);
    const singleAccess = items.filter((i) => i.kind === 'shared' && i.accessUids.length === 1);
    const noOwner = items.filter((i) => !i.ownerUid && i.status !== 'cancelled');

    return {
      active, trials, cancelled, free, purchases,
      monatlich, jaehrlich, sonstige,
      monatlichMoney, jaehrlichMoney, sonstigeMoney, jahresMoney, trialAnnual, einmaligMoney,
      categories, payments, gemischteRhythmen, billingDays, withoutBillingDay,
      dated, idleSeats, idleWaste,
      needsRotation, singleAccess, noOwner,
    };
  }, [items]);

  const userName = (uid: string | null): string =>
    users.find((u) => u.uid === uid)?.name ?? 'niemand';

  const max = stats.categories[0]?.annualCents ?? 1;

  if (!isAdmin) return <MemberDashboard onNavigate={onNavigate} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Übersicht</h1>
        </div>
      </div>

      <div className="grid grid-kpi">
        <div className="card">
          <div className="kpi-label">Monatlich</div>
          <div className="kpi-value">{formatMoney(stats.monatlichMoney.grossCents)}</div>
          <div className="kpi-hint">
            {stats.monatlich.length === 0
              ? 'keine monatlichen Verträge'
              : `${plural(stats.monatlich.length, 'Vertrag', 'Verträge')} · brutto, jeden Monat`}
          </div>
        </div>
        <div className="card">
          <div className="kpi-label">Jährlich</div>
          <div className="kpi-value">{formatMoney(stats.jaehrlichMoney.grossCents)}</div>
          <div className="kpi-hint">
            {stats.jaehrlich.length === 0
              ? 'keine Jahresverträge'
              : `${plural(stats.jaehrlich.length, 'Vertrag', 'Verträge')} · brutto, einmal im Jahr`}
          </div>
        </div>
        <div className="card">
          <div className="kpi-label">Aufs Jahr gesamt</div>
          <div className="kpi-value">{formatMoney(stats.jahresMoney.grossCents)}</div>
          <div className="kpi-hint">
            alle Rhythmen zusammengerechnet
            {stats.sonstige.length > 0
              ? `, inkl. ${plural(stats.sonstige.length, 'Vertrag', 'Verträgen')} in anderem Takt`
              : ''}
          </div>
        </div>
      </div>

      {isAdmin ? (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="card-title">
            <h2>Netto, Umsatzsteuer, Brutto</h2>
          </div>
          <table>
            <thead>
              <tr>
                <th>Zeitraum</th>
                <th className="num">Netto</th>
                <th className="num">Umsatzsteuer</th>
                <th className="num">Brutto</th>
              </tr>
            </thead>
            <tbody>
              {([
                ['Monatlich abgebucht', stats.monatlichMoney],
                ['Jährlich abgebucht', stats.jaehrlichMoney],
                ...(stats.sonstige.length > 0
                  ? ([['Andere Rhythmen, je Abrechnung', stats.sonstigeMoney]] as [string, Money][])
                  : []),
                ['Aufs Jahr gerechnet', stats.jahresMoney],
                ['Einmalig gekauft', stats.einmaligMoney],
              ] as [string, Money][]).map(([label, money]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td className="num mono">{formatMoney(money.netCents)}</td>
                  <td className="num mono">{formatMoney(money.vatCents)}</td>
                  <td className="num mono">
                    <strong>{formatMoney(money.grossCents)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}


      {duplicates.groups.length > 0 ? (
        <Hint
          id="duplicates"
          tone="danger"
          count={affectedCount(duplicates)}
          showLabel="Betroffene zeigen"
          onShow={() =>
            onNavigate('vault', [...new Set(duplicates.groups.flatMap((g) => g.uses.map((u) => u.itemId)))])
          }
          title={
            <>
              {plural(duplicates.groups.length, 'Passwort wird', 'Passwörter werden')} mehrfach
              benutzt – an insgesamt {affectedCount(duplicates)} Stellen.
            </>
          }
        >
          {duplicates.groups.slice(0, 5).map((g, i) => (
            <div key={g.id} style={{ marginTop: i === 0 ? 0 : 4 }}>
              {g.uses.map((u) => `${u.title}${u.where === 'Eintrag' ? '' : ` (${u.where})`}`).join(' = ')}
            </div>
          ))}
          {duplicates.groups.length > 5 ? <div style={{ marginTop: 4 }}>…</div> : null}
          <p style={{ margin: '8px 0 0' }}>
            Wird eines davon bekannt, sind alle betroffen. Der Passwortgenerator im Editor macht
            daraus in zwei Klicks eigene Passwörter.
          </p>
        </Hint>
      ) : null}

      {stats.trials.length > 0 ? (
        <div className="note note-warn" style={{ marginTop: 14 }}>
          <strong>{plural(stats.trials.length, 'Testphase läuft', 'Testphasen laufen')}.</strong>{' '}
          Wenn nichts gekündigt wird, kommen{' '}
          {formatMoney(stats.trialAnnual.grossCents)} im Jahr dazu.
        </div>
      ) : null}

      {stats.needsRotation.length > 0 ? (
        <Hint
          id="rotation"
          tone="danger"
          count={stats.needsRotation.length}
          onShow={() => onNavigate('vault', stats.needsRotation.map((i) => i.id))}
          title={
            <>
              {plural(stats.needsRotation.length, 'Passwort muss', 'Passwörter müssen')} beim
              Anbieter geändert werden.
            </>
          }
        >
          Jemandem wurde der Zugriff entzogen – solange das Geheimnis gleich bleibt, nützt das
          nichts.
        </Hint>
      ) : null}

      <div className="grid grid-2" style={{ marginTop: 14 }}>
        <div className="card">
          <div className="card-title">
            <h2>Kosten nach Kategorie</h2>
            <span className="small muted">brutto aufs Jahr</span>
          </div>
          {stats.categories.length === 0 ? (
            <div className="empty">Noch keine aktiven Verträge erfasst.</div>
          ) : (
            <div className="stack">
              {stats.categories.map((c) => (
                <div key={c.name}>
                  <div className="row small" style={{ marginBottom: 5 }}>
                    <strong>{c.name}</strong>
                    <span className="muted">({c.count})</span>
                    <span className="spacer" />
                    <span className="mono">{formatMoney(c.annualCents)}</span>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill"
                      style={{ width: `${Math.max(2, (c.annualCents / max) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-title">
            <h2>Nächste 60 Tage</h2>
            <span className="small muted">Verlängerungen und Fristen</span>
          </div>
          {stats.dated.length === 0 ? (
            <div className="empty">Nichts Dringendes. Trage Verlängerungsdaten ein, damit hier etwas erscheint.</div>
          ) : (
            <table>
              <tbody>
                {stats.dated.slice(0, 10).map(({ item, kind, days }) => (
                  <tr key={`${item.id}-${kind}`}>
                    <td>
                      <strong>{item.title}</strong>
                      <div className="small muted">
                        {kind === 'deadline' ? 'Kündigungsfrist' : 'Verlängerung'} ·{' '}
                        {CYCLE_LABEL[item.cycle]}
                      </div>
                    </td>
                    <td className="num">
                      <span
                        className={
                          days < 0
                            ? 'badge badge-danger'
                            : days <= 14
                              ? 'badge badge-warn'
                              : 'badge'
                        }
                      >
                        {days < 0 ? `${Math.abs(days)} Tage überfällig` : days === 0 ? 'heute' : `in ${days} Tagen`}
                      </span>
                    </td>
                    <td className="num mono">{formatMoney(item.priceCents, item.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="grid grid-2" style={{ marginTop: 14 }}>
        <div className="card">
          <div className="card-title">
            <h2>Womit bezahlt wird</h2>
            <span className="small muted">brutto</span>
          </div>
          {stats.payments.length === 0 ? (
            <div className="empty">Noch keine laufenden Verträge erfasst.</div>
          ) : (
            <>
              <table>
                <thead>
                  <tr>
                    <th />
                    <th className="num">im Monatsmittel</th>
                    <th className="num">aufs Jahr</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.payments.map((p) => (
                    <tr key={p.method}>
                      <td>
                        <strong>{PAYMENT_LABEL[p.method]}</strong>
                        <div className="small muted">
                          {plural(p.count, 'Vertrag', 'Verträge')}
                        </div>
                      </td>
                      <td className="num mono muted">{formatMoney(monthlyAverage(p.annualCents))}</td>
                      <td className="num mono">
                        <strong>{formatMoney(p.annualCents)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {stats.gemischteRhythmen ? (
                <p className="small muted" style={{ marginTop: 10 }}>
                  Der Monatswert ist ein Zwölftel des Jahresbetrags, also ein Mittel. Jahres- und
                  Quartalsverträge werden natürlich nicht monatlich abgebucht, sondern einmal in
                  voller Höhe – wann genau, steht in den Abbuchungstagen und beim Eintrag selbst.
                </p>
              ) : null}
            </>
          )}
        </div>

        <div className="card">
          <div className="card-title">
            <h2>Abbuchungstage</h2>
            <span className="small muted">monatliche Verträge, brutto</span>
          </div>
          {stats.billingDays.length === 0 ? (
            <div className="empty">
              Noch kein Abbuchungstag eingetragen. Trag ihn im Eintrag ein, dann siehst du hier,
              wann was vom Konto geht.
            </div>
          ) : (
            <>
              <table>
                <tbody>
                  {stats.billingDays.map((d) => (
                    <tr key={d.day}>
                      <td style={{ width: 60 }}>
                        <strong>{d.day}.</strong>
                      </td>
                      <td className="small">
                        {d.items.map((i) => i.title).join(', ')}
                      </td>
                      <td className="num mono">{formatMoney(d.cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {stats.withoutBillingDay > 0 ? (
                <p className="small muted" style={{ marginTop: 10 }}>
                  Bei {plural(stats.withoutBillingDay, 'monatlichen Vertrag', 'monatlichen Verträgen')}{' '}
                  fehlt der Abbuchungstag noch.
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-title">
          <h2>Was auffällt</h2>
        </div>
        <div className="stack">
          {stats.idleSeats.length > 0 ? (
            <Hint
              id="idleSeats"
              count={stats.idleSeats.length}
              onShow={() => onNavigate('vault', stats.idleSeats.map((r) => r.item.id))}
              title={
                <>
                  Ihr bezahlt Plätze, die niemand nutzt
                  {stats.idleWaste > 0 ? ` – rund ${formatMoney(stats.idleWaste)} im Jahr` : ''}.
                </>
              }
            >
              {stats.idleSeats.slice(0, 5).map((r) => `${r.item.title}: ${r.spare} frei`).join(' · ')}
              {stats.idleSeats.length > 5 ? ' …' : ''}
            </Hint>
          ) : null}

          {stats.singleAccess.length > 0 ? (
            <Hint
              id="singleAccess"
              count={stats.singleAccess.length}
              onShow={() => onNavigate('vault', stats.singleAccess.map((i) => i.id))}
              title={
                <>
                  {plural(stats.singleAccess.length, 'Zugang hängt', 'Zugänge hängen')} an einer
                  einzigen Person.
                </>
              }
            >
              Fällt sie aus oder verliert sie ihre Passphrase, ist das Passwort weg.
            </Hint>
          ) : null}

          {stats.noOwner.length > 0 ? (
            <Hint
              id="noOwner"
              tone="info"
              count={stats.noOwner.length}
              onShow={() => onNavigate('vault', stats.noOwner.map((i) => i.id))}
              title={
                <>
                  {plural(stats.noOwner.length, 'Vertrag hat', 'Verträge haben')} keine
                  verantwortliche Person.
                </>
              }
            >
              Ohne Zuständigkeit merkt niemand, wenn sich ein Preis ändert oder eine Frist abläuft.
            </Hint>
          ) : null}

          {stats.free.length > 0 ? (
            <Hint
              id="freeAccess"
              tone="info"
              count={stats.free.length}
              onShow={() => onNavigate('vault', stats.free.map((i) => i.id))}
              title={
                <>{plural(stats.free.length, 'Zugang kostet', 'Zugänge kosten')} nichts.</>
              }
            >
              Shop-Konten, Behördenportale und Ähnliches. Sie tauchen in keiner Kostenrechnung auf.
            </Hint>
          ) : null}

          {stats.cancelled.length > 0 ? (
            <div className="note note-ok">
              <strong>{plural(stats.cancelled.length, 'gekündigter Vertrag', 'gekündigte Verträge')}</strong>{' '}
              zählt nicht mehr in die Summe – zur Dokumentation bleibt der Eintrag stehen.
            </div>
          ) : null}

          {stats.singleAccess.length === 0 &&
          stats.noOwner.length === 0 &&
          stats.cancelled.length === 0 &&
          stats.idleSeats.length === 0 &&
          stats.free.length === 0 ? (
            <div className="empty">Nichts zu meckern.</div>
          ) : null}
        </div>
        <HiddenHints />
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-title">
          <h2>Teuerste Verträge</h2>
          <span className="small muted">brutto aufs Jahr</span>
        </div>
        {stats.active.length === 0 ? (
          <div className="empty">Noch nichts erfasst.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Dienst</th>
                <th className="col-optional">Verantwortlich</th>
                <th className="col-optional">Abrechnung</th>
                <th className="num">aufs Jahr</th>
              </tr>
            </thead>
            <tbody>
              {[...stats.active]
                .sort((a, b) => annualMoney(b).grossCents - annualMoney(a).grossCents)
                .slice(0, 8)
                .map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="row-tight">
                        <ItemIcon item={item} size={22} />
                        <div>
                          <strong>{item.title}</strong>
                          {item.category ? (
                            <div className="small muted">{item.category}</div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="small muted col-optional">{userName(item.ownerUid)}</td>
                    <td className="small muted col-optional">
                      {formatMoney(item.priceCents, item.currency)} {CYCLE_LABEL[item.cycle]}
                      {item.seats ? ` · ${item.seats} Plätze` : ''}
                    </td>
                    <td className="num mono">{formatMoney(annualMoney(item).grossCents)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
